import get from 'lodash/get';
import moment from 'moment-timezone';
import {
  amazonCancelRefundAsurionOrderAutomatic,
  amazonGetCancelRefundAsurionOrderInformation,
} from '../AmazonActions';
import * as asurionStatuses from '../../../constants/asurionStatuses';
import * as ordersActions from '../OrdersSearchActions';
import { buildDecisionTreePath, UnexpectedDecisionTreeEdge, UnhandledDecisionTreeCase } from '../../../util';
import { extractCurrentMacroWizardPath, extractDecisionTreeOfSelectedTicket } from '../../../extractFromState';
import { addEdgeToDecisionTreePath } from '../../searchActions';
import { decisionTreeCaseNotHandled } from '../../index';
import { showErrorPopup } from '../../../components/popups/ErrorPopup/ErrorPopup';
import { handleError } from 'services/ErrorService/ErrorService';
import { getCurrencyCode, getRefundAmount, getSelectedOrder } from '../../../selectors/tools';

export const processAmazonOrderCancellation = (isAutoSearch, orderNumber) => async (dispatch, getState) => {
  try {
    const orderInfo = await dispatch(amazonGetCancelRefundAsurionOrderInformation(orderNumber));
    const selectedOrder = get(orderInfo, 'order');
    const orderTotal = get(selectedOrder, 'OrderTotal.Amount');
    const orderItems = get(orderInfo, 'orderItems');
    const data = { orderInfo, selectedOrder, orderItems, orderTotal };

    for (let key in data) {
      if (!data[key]) {
        throw new Error(`${key} is undefined`);
      }
    }

    // Run checks to get order state
    const isRefundableOrCancellable = ['UNSHIPPED_CANCEL', 'SHIPPED_FULL_REFUND', 'SHIPPED_PARTIAL_REFUND'].includes(
      orderInfo.decision
    );
    const isRefundAlreadyScheduled = ['ALREADY_PROCESSED'].includes(orderInfo.decision);
    const isCancellable = ['UNSHIPPED_CANCEL'].includes(orderInfo.decision);
    const isRefundable = ['SHIPPED_FULL_REFUND', 'SHIPPED_PARTIAL_REFUND'].includes(orderInfo.decision);
    const isNoRefunds = ['SHIPPED_NO_REFUND'].includes(orderInfo.decision);

    const isOrderAlreadyCanceled = ['Canceled', asurionStatuses.CANCELLED].includes(selectedOrder.OrderStatus);
    const isOrderPending = ['Pending'].includes(selectedOrder.OrderStatus);
    const isCancellationInProcess = [asurionStatuses.CLAIM_EXIST].includes(selectedOrder.OrderStatus);
    const isCaseOpened = [asurionStatuses.CASE_OPENED].includes(selectedOrder.OrderStatus);
    const isPurchaseDateOver12months = moment().diff(orderInfo.purchaseDate, 'months', true) > 12;

    // get order country US - first number 1; CA - first number 7
    const orderFirstNumber = parseInt(selectedOrder.AmazonOrderId.slice(0, 1));
    let orderCountry;

    if (orderFirstNumber === 1) {
      orderCountry = 'US';
    } else if (orderFirstNumber === 7) {
      orderCountry = 'CA';
    } else {
      throw new Error('Unknown country code');
    }

    // based on order state get the next edge text
    let nextEdgeText;

    if (isNoRefunds) {
      nextEdgeText = 'no_refunds';
    } else if (isOrderAlreadyCanceled) {
      nextEdgeText = 'order_already_cancelled';
    } else if (isRefundAlreadyScheduled) {
      nextEdgeText = 'refund_already_scheduled';
    } else if (isOrderPending) {
      // backwards compatible
      nextEdgeText = 'order_pending';
    } else if (isCancellationInProcess) {
      // cancellation has been received and is being processed
      nextEdgeText = 'cancellation_in_process';
    } else if (isCaseOpened) {
      // request has been received but no further action is approved yet
      nextEdgeText = 'case_opened';
    } else if (isRefundableOrCancellable) {
      if (isRefundable) {
        // if order is refundable update refundAmount, currencyCode, totalYears in state
        const { CurrencyCode } = selectedOrder.OrderTotal && selectedOrder.OrderTotal;
        const { orderItemsDetails } = orderInfo;
        const { refundAmount, totalYears } = orderItemsDetails[0];
        const refundData = { orderItemsDetails, refundAmount, totalYears, CurrencyCode };

        for (let key in refundData) {
          if (!refundData[key]) {
            throw new Error(`${key} is undefined`);
          }
        }

        dispatch(ordersActions.setRefundAmount(refundAmount));
        dispatch(ordersActions.setCurrencyCode(CurrencyCode));
        dispatch(ordersActions.setAmazonOrderWarrantyTotalYears(totalYears));
      }

      // if order is cancallable (UNSHIPPED_CANCEL), use Amount as refundAmount
      if (isCancellable) {
        const { Amount, CurrencyCode } = selectedOrder.OrderTotal;

        if (!Amount || !CurrencyCode) {
          throw new Error(`Amount or CurrencyCode is undefined`);
        }

        dispatch(ordersActions.setCurrencyCode(CurrencyCode));
        dispatch(ordersActions.setRefundAmount(Amount));
      }

      if (isPurchaseDateOver12months) {
        // PARTIAL REFUND
        nextEdgeText = 'order_purchase_date_over_12_months';
      } else if (orderCountry === 'US') {
        // FULL REFUND
        nextEdgeText = 'order_country_us';
      } else if (orderCountry === 'CA') {
        // FULL REFUND
        nextEdgeText = 'order_country_ca';
      }

      // escalate ticket if multiple orders
      if (orderItems.length > 1) {
        nextEdgeText = 'multiple_orders';
      }
    }

    if (!nextEdgeText) {
      throw new UnexpectedDecisionTreeEdge(nextEdgeText);
    }

    const state = getState();
    const partnerTrees = extractDecisionTreeOfSelectedTicket(state);
    const selectedTag = extractCurrentMacroWizardPath(state);
    const selectedDecisionTree = get(partnerTrees, selectedTag);
    const fullDecisionTreePath =
      isAutoSearch && selectedDecisionTree && buildDecisionTreePath(selectedDecisionTree, nextEdgeText);

    // order total is not contained in order data for Asurion - info needed in widget
    dispatch(ordersActions.setOrderTotal(orderTotal));

    // if using automatic search, use full decisionTreePath
    nextEdgeText = fullDecisionTreePath || nextEdgeText;

    dispatch(addEdgeToDecisionTreePath(nextEdgeText));
  } catch (error) {
    handleError({
      error,
      customMessage: 'Error in processAmazonOrderCancellation',
      customFields: { isAutoSearch, orderNumber },
    });
    if (!isAutoSearch) {
      return error instanceof UnhandledDecisionTreeCase
        ? dispatch(decisionTreeCaseNotHandled(error.message))
        : showErrorPopup(`Something went wrong`, 'Error');
    }
  }
};

export const refundAmazonOrder = () => async (dispatch, getState) => {
  const state = getState();
  const { AmazonOrderId, PurchaseDate } = getSelectedOrder(state);
  const refundAmount = getRefundAmount(state);
  const currencyCode = getCurrencyCode(state);

  const selectedOrder = { AmazonOrderId, PurchaseDate, refundAmount, currencyCode };

  for (let key in selectedOrder) {
    if (!selectedOrder[key]) {
      throw new Error(`${key} is undefined`);
    }
  }

  await dispatch(amazonCancelRefundAsurionOrderAutomatic());
};
